import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Radar,
  RefreshCw,
  Power,
  Save,
  ExternalLink,
  Rocket,
  Trash2,
  Undo2,
  AlertCircle,
  CheckCircle2,
  Globe,
  Gauge,
  Bot
} from "lucide-react";
import { Button } from "./ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authHeaders = () => {
  const token = localStorage.getItem("cortoviral_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const fmt = (n) => {
  if (n === null || n === undefined) return "—";
  if (n >= 1e6) return `${(n / 1e6).toFixed(1).replace(".", ",")} M`;
  if (n >= 1e3) return `${Math.round(n / 1e3)} K`;
  return String(n);
};

const STATUS_TABS = [
  { key: "ready", label: "Listos" },
  { key: "used", label: "Usados" },
  { key: "new", label: "Sin evaluar" },
  { key: "discarded", label: "Descartados" },
  { key: "failed", label: "Fallidos" }
];

const STATUS_LABEL = { ready: "Listo", used: "Usado", new: "Sin evaluar", discarded: "Descartado", failed: "Fallido" };

const STATUS_STYLE = {
  ready: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  used: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  new: "bg-slate-700/40 text-slate-300 border-white/10",
  discarded: "bg-slate-800 text-slate-500 border-white/5",
  failed: "bg-red-500/15 text-red-300 border-red-500/30"
};

export const RadarView = ({ onShortCreated }) => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [tab, setTab] = useState("ready");
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const load = useCallback(async (statusFilter) => {
    try {
      const res = await axios.get(`${API}/admin/radar`, {
        params: { status_filter: statusFilter },
        headers: authHeaders(),
        withCredentials: true
      });
      setData(res.data);
      setForm((prev) => prev || res.data.settings);
    } catch (e) {
      setFeedback({ type: "error", text: e.response?.data?.detail || "No se pudo cargar el Radar." });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(tab);
  }, [tab, load]);

  const saveConfig = async (patch) => {
    setSaving(true);
    setFeedback(null);
    try {
      const body = patch || {
        scan_interval_hours: Number(form.scan_interval_hours),
        searches_per_scan: Number(form.searches_per_scan),
        regions: form.regions,
        niches: form.niches,
        max_age_hours: Number(form.max_age_hours),
        min_views: Number(form.min_views),
        feed_autopilot: form.feed_autopilot,
        watermark: form.watermark
      };
      const res = await axios.post(`${API}/admin/radar/config`, body, { headers: authHeaders(), withCredentials: true });
      setForm(res.data.settings);
      setData((d) => (d ? { ...d, settings: res.data.settings } : d));
      if (!patch) setFeedback({ type: "success", text: "Configuración del Radar guardada." });
    } catch (e) {
      setFeedback({ type: "error", text: e.response?.data?.detail || "Error guardando la configuración." });
    } finally {
      setSaving(false);
    }
  };

  const scanNow = async () => {
    setScanning(true);
    setFeedback(null);
    try {
      const res = await axios.post(`${API}/admin/radar/scan`, {}, { headers: authHeaders(), withCredentials: true });
      const r = res.data;
      setFeedback({
        type: r.errors?.length ? "error" : "success",
        text: `Escaneo hecho: ${r.videos} Shorts encontrados (${r.new} nuevos), ${r.ready} temas listos, ${r.units} unidades de cuota.` +
          (r.errors?.length ? ` Avisos: ${r.errors.join("; ")}` : "")
      });
      setTab("ready");
      load("ready");
    } catch (e) {
      setFeedback({ type: "error", text: e.response?.data?.detail || "Error en el escaneo." });
      load(tab);
    } finally {
      setScanning(false);
    }
  };

  const publishTrend = async (trend) => {
    if (!window.confirm(`Se creará un Short ORIGINAL sobre:\n\n"${trend.topic_es}"\n\ny se publicará en tu canal. ¿Continuar?`)) return;
    setBusyId(trend.id);
    setFeedback(null);
    try {
      const res = await axios.post(`${API}/admin/radar/trends/${trend.id}/publish`, {}, { headers: authHeaders(), withCredentials: true });
      const s = res.data;
      setFeedback({
        type: "success",
        text: s.status === "published" ? `Publicado: ${s.youtube_url}` : `Short creado (${s.progress_message || s.status}).`
      });
      if (onShortCreated) onShortCreated(s);
      load(tab);
    } catch (e) {
      setFeedback({ type: "error", text: e.response?.data?.detail || "Error creando el Short." });
      load(tab);
    } finally {
      setBusyId(null);
    }
  };

  const decide = async (trend, decision) => {
    setBusyId(trend.id);
    try {
      await axios.post(`${API}/admin/radar/trends/${trend.id}/${decision}`, {}, { headers: authHeaders(), withCredentials: true });
      load(tab);
    } catch (e) {
      setFeedback({ type: "error", text: e.response?.data?.detail || "Error." });
    } finally {
      setBusyId(null);
    }
  };

  const toggleList = (field, key) => {
    setForm((f) => {
      const cur = f[field] || [];
      return { ...f, [field]: cur.includes(key) ? cur.filter((x) => x !== key) : [...cur, key] };
    });
  };

  if (loading || !data || !form) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-400 text-sm gap-2">
        <RefreshCw className="w-4 h-4 animate-spin" /> Cargando Radar Viral...
      </div>
    );
  }

  const s = data.settings;
  const q = data.quota;
  const quotaPct = Math.min(100, Math.round((q.used / Math.max(q.limit, 1)) * 100));
  const nicheEntries = Object.entries(data.niches);

  return (
    <div data-testid="radar-view" className="space-y-6">
      {/* Cabecera y estado */}
      <div className="bg-gradient-to-br from-[#12141D] to-[#171B2B] border border-amber-500/30 rounded-2xl p-6 shadow-2xl">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-amber-500/15 border border-amber-500/40 text-amber-400 flex items-center justify-center shrink-0">
              <Radar className={`w-6 h-6 ${s.enabled ? "animate-pulse" : ""}`} />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-lg font-black text-white">Radar Viral</h3>
                <span className="text-[10px] uppercase font-mono font-bold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30">
                  Solo admin
                </span>
                <span className={`text-[10px] uppercase font-mono font-bold px-2 py-0.5 rounded-full ${
                  s.enabled ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/40" : "bg-slate-800 text-slate-400"
                }`}>
                  {s.enabled ? `Encendido · busca cada ${s.scan_interval_hours} h` : "Apagado · no gasta cuota"}
                </span>
              </div>
              <p className="text-xs text-slate-400 mt-0.5 max-w-2xl">
                Detecta los Shorts que están despegando en YouTube y la IA propone un tema propio en español para cada uno.
                Nunca se descarga ni se reutiliza el vídeo original: el Piloto Automático crea un Short nuevo.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              data-testid="radar-toggle-btn"
              role="switch"
              aria-checked={!!s.enabled}
              onClick={() => saveConfig({ enabled: !s.enabled })}
              disabled={saving}
              title={s.enabled ? "Apagar el Radar: deja de buscar y no gasta cuota de YouTube" : "Encender el Radar: empieza a buscar tendencias"}
              className={`h-9 pl-1 pr-3 rounded-full border flex items-center gap-2 text-xs font-black transition-colors disabled:opacity-60 ${
                s.enabled ? "bg-emerald-600/20 border-emerald-500/50 text-emerald-300" : "bg-slate-900 border-white/15 text-slate-400"
              }`}
            >
              <span className={`w-12 h-7 rounded-full relative transition-colors ${s.enabled ? "bg-emerald-500" : "bg-slate-700"}`}>
                <span className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow flex items-center justify-center transition-all ${s.enabled ? "left-6" : "left-1"}`}>
                  <Power className={`w-3 h-3 ${s.enabled ? "text-emerald-600" : "text-slate-500"}`} />
                </span>
              </span>
              {s.enabled ? "ENCENDIDO" : "APAGADO"}
            </button>
            <Button
              data-testid="radar-scan-now-btn"
              onClick={scanNow}
              disabled={!s.enabled || scanning || data.scanning}
              title={s.enabled ? "Buscar tendencias ahora (gasta cuota)" : "Enciende el Radar para poder buscar"}
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-slate-950 font-black text-xs flex items-center gap-1.5"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${scanning ? "animate-spin" : ""}`} />
              {scanning ? "Escaneando YouTube..." : "Escanear ahora"}
            </Button>
          </div>
        </div>

        {feedback && (
          <div className={`mt-4 p-3 rounded-xl text-xs flex items-start gap-2 break-words ${
            feedback.type === "success" ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-300" : "bg-red-500/10 border border-red-500/30 text-red-300"
          }`}>
            {feedback.type === "success" ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" /> : <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />}
            <span className="min-w-0">{feedback.text}</span>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-5">
          <div className="p-3.5 rounded-xl bg-black/40 border border-white/5">
            <span className="text-[10px] uppercase font-bold text-slate-400 flex items-center gap-1.5"><Gauge className="w-3.5 h-3.5 text-amber-400" /> Cuota de búsqueda hoy</span>
            <div className="mt-1.5 h-1.5 rounded-full bg-slate-800 overflow-hidden">
              <div className={`h-full ${quotaPct > 85 ? "bg-red-500" : "bg-amber-400"}`} style={{ width: `${quotaPct}%` }} />
            </div>
            <span className="text-xs font-mono text-white block mt-1">{q.used} / {q.limit} unidades</span>
            {!s.enabled && (
              <span className="text-[10px] text-emerald-400 block">Radar apagado: no se gasta ninguna unidad.</span>
            )}
            <span className="text-[10px] text-slate-500 block">
              {q.source === "api_key" ? "Clave de API propia (cuota separada de las subidas)" : "Usa el OAuth del canal: comparte cuota con las subidas"}
            </span>
          </div>
          <div className="p-3.5 rounded-xl bg-black/40 border border-white/5">
            <span className="text-[10px] uppercase font-bold text-slate-400 flex items-center gap-1.5"><Globe className="w-3.5 h-3.5 text-cyan-400" /> Tendencias</span>
            <span className="text-xs text-white block mt-1.5">
              <strong className="text-emerald-400">{data.counts.ready}</strong> listas · {data.counts.used} usadas · {data.counts.new} sin evaluar
            </span>
            <span className="text-[10px] text-slate-500 block mt-0.5">
              Último escaneo: {s.last_scan ? new Date(s.last_scan).toLocaleString("es-ES") : "nunca"}
            </span>
            {s.last_error && <span className="text-[10px] text-red-400 block mt-0.5 break-words">{s.last_error}</span>}
          </div>
          <div className="p-3.5 rounded-xl bg-black/40 border border-white/5">
            <span className="text-[10px] uppercase font-bold text-slate-400 flex items-center gap-1.5"><Bot className="w-3.5 h-3.5 text-red-400" /> Piloto Automático</span>
            <span className="text-xs text-white block mt-1.5">
              {s.feed_autopilot ? "Usa los temas del Radar (el de mayor puntuación)" : "Usa la lista fija de temas"}
            </span>
            <span className="text-[10px] text-slate-500 block mt-0.5">
              Con el Radar apagado sigue usando los temas que ya encontró (no gasta cuota de búsqueda). Si no quedan, usa la lista fija.
            </span>
          </div>
        </div>
      </div>

      {/* Configuración */}
      <div className="bg-[#12141D] border border-white/10 rounded-2xl p-6 space-y-5">
        <h4 className="text-sm font-black text-white">Configuración</h4>

        <div>
          <span className="text-[11px] uppercase font-bold text-slate-400 block mb-2">
            Nichos que vigila {form.niches.length === 0 && <span className="text-amber-400 normal-case">(todos, incluido lo viral sin categoría)</span>}
          </span>
          <div className="flex flex-wrap gap-1.5">
            <button
              onClick={() => setForm((f) => ({ ...f, niches: [] }))}
              className={`px-2.5 py-1 rounded-lg text-xs font-semibold border ${
                form.niches.length === 0 ? "bg-amber-500 text-slate-950 border-amber-400" : "bg-slate-900 text-slate-400 border-white/10 hover:text-white"
              }`}
            >
              Todos
            </button>
            {nicheEntries.map(([key, label]) => (
              <button
                key={key}
                onClick={() => toggleList("niches", key)}
                className={`px-2.5 py-1 rounded-lg text-xs font-semibold border ${
                  form.niches.includes(key) ? "bg-amber-500/20 text-amber-300 border-amber-500/50" : "bg-slate-900 text-slate-400 border-white/10 hover:text-white"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <span className="text-[11px] uppercase font-bold text-slate-400 block mb-2">Países donde busca tendencias</span>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(data.regions).map(([code, name]) => (
              <button
                key={code}
                onClick={() => toggleList("regions", code)}
                className={`px-2.5 py-1 rounded-lg text-xs font-semibold border ${
                  form.regions.includes(code) ? "bg-cyan-500/20 text-cyan-300 border-cyan-500/50" : "bg-slate-900 text-slate-400 border-white/10 hover:text-white"
                }`}
              >
                {name}
              </button>
            ))}
          </div>
          <p className="text-[10px] text-slate-500 mt-1.5">
            Las tendencias de países de habla inglesa son oportunidades: la IA las adapta al español antes de que lleguen.
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          {[
            { field: "scan_interval_hours", label: "Escanear cada (h)", min: 2, max: 48 },
            { field: "searches_per_scan", label: "Búsquedas", min: 1, max: 20, hint: "Por escaneo; 100 unidades cada una" },
            { field: "max_age_hours", label: "Antigüedad máx. (h)", min: 6, max: 168 },
            { field: "min_views", label: "Visitas mínimas", min: 0, max: 100000000 }
          ].map(({ field, label, min, max, hint }) => (
            <label key={field} className="block">
              <span className="text-[11px] uppercase font-bold text-slate-400 block mb-1">{label}</span>
              <input
                type="number"
                min={min}
                max={max}
                value={form[field]}
                onChange={(e) => setForm((f) => ({ ...f, [field]: e.target.value }))}
                className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-500/60"
              />
              {hint && <span className="text-[10px] text-slate-500">{hint}</span>}
            </label>
          ))}
          <label className="block col-span-2 lg:col-span-1">
            <span className="text-[11px] uppercase font-bold text-slate-400 block mb-1">Marca de agua</span>
            <input
              type="text"
              maxLength={40}
              placeholder="@TuCanal"
              value={form.watermark}
              onChange={(e) => setForm((f) => ({ ...f, watermark: e.target.value }))}
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-500/60"
            />
          </label>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-2 border-t border-white/5">
          <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
            <input
              type="checkbox"
              checked={!!form.feed_autopilot}
              onChange={(e) => setForm((f) => ({ ...f, feed_autopilot: e.target.checked }))}
              className="accent-amber-500 w-4 h-4"
            />
            El Piloto Automático usa los temas del Radar
          </label>
          <Button
            data-testid="radar-save-btn"
            onClick={() => saveConfig()}
            disabled={saving}
            className="bg-slate-100 hover:bg-white text-slate-950 font-bold text-xs flex items-center gap-1.5"
          >
            <Save className="w-3.5 h-3.5" /> {saving ? "Guardando..." : "Guardar configuración"}
          </Button>
        </div>
      </div>

      {/* Tendencias */}
      <div className="space-y-3">
        <div className="flex items-center gap-2 overflow-x-auto pb-1 text-xs">
          {STATUS_TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-3 py-1.5 rounded-lg font-semibold whitespace-nowrap ${
                tab === t.key ? "bg-amber-500 text-slate-950" : "bg-slate-900/60 text-slate-400 hover:text-white"
              }`}
            >
              {t.label} ({data.counts[t.key] ?? 0})
            </button>
          ))}
        </div>

        {data.trends.length === 0 ? (
          <div className="text-center py-12 text-sm text-slate-500 bg-[#12141D] border border-white/5 rounded-2xl">
            No hay tendencias en esta lista. Pulsa «Escanear ahora» para buscar.
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {data.trends.map((t) => (
              <div key={t.id} className="bg-[#12141D] border border-white/10 rounded-xl p-3 flex gap-3 min-w-0">
                <a href={t.url} target="_blank" rel="noopener noreferrer" className="shrink-0 self-start relative" title="Ver el Short original en YouTube (solo como referencia)">
                  {t.thumbnail ? (
                    <img src={t.thumbnail} alt="" loading="lazy" className="w-24 h-[72px] sm:w-28 sm:h-20 object-cover rounded-lg bg-slate-800" />
                  ) : (
                    <div className="w-24 h-[72px] sm:w-28 sm:h-20 rounded-lg bg-slate-800" />
                  )}
                  <span className="absolute bottom-1 right-1 text-[10px] font-mono font-bold bg-black/80 text-amber-300 px-1 rounded">{t.score}</span>
                </a>
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded border ${STATUS_STYLE[t.status] || STATUS_STYLE.new}`}>{STATUS_LABEL[t.status] || t.status}</span>
                    {t.category && <span className="text-[10px] uppercase font-bold text-amber-300">{data.niches[t.category] || t.category}</span>}
                    <span className="text-[10px] text-slate-500">{t.region} · {t.lang}</span>
                  </div>
                  {t.topic_es ? (
                    <p className="text-sm font-bold text-white leading-snug break-words">{t.topic_es}</p>
                  ) : null}
                  <a href={t.url} target="_blank" rel="noopener noreferrer" className="text-[11px] text-slate-400 hover:text-slate-200 flex items-start gap-1 min-w-0">
                    <ExternalLink className="w-3 h-3 shrink-0 mt-0.5" />
                    <span className="truncate">Original: {t.title} — {t.channel_title}</span>
                  </a>
                  <div className="text-[11px] font-mono text-slate-400 flex flex-wrap gap-x-3">
                    <span>{fmt(t.views)} visitas</span>
                    <span>{fmt(t.vph)}/h</span>
                    <span title="Visitas frente a suscriptores del canal">×{t.outlier} outlier</span>
                    <span>{Math.round(t.hours)} h</span>
                  </div>
                  {t.reason && <p className="text-[11px] text-slate-500 break-words">{t.reason}</p>}
                  {t.last_error && t.status !== "used" && <p className="text-[11px] text-red-400 break-words">{t.last_error}</p>}
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {(t.status === "ready" || t.status === "failed") && t.topic_es && (
                      <Button
                        size="sm"
                        onClick={() => publishTrend(t)}
                        disabled={busyId !== null}
                        className="h-7 px-2.5 text-[11px] font-bold bg-red-600 hover:bg-red-500 text-white flex items-center gap-1"
                      >
                        {busyId === t.id ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Rocket className="w-3 h-3" />}
                        {busyId === t.id ? "Creando y publicando..." : "Crear y publicar ahora"}
                      </Button>
                    )}
                    {t.status !== "discarded" && t.status !== "used" && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => decide(t, "discard")}
                        disabled={busyId !== null}
                        className="h-7 px-2 text-[11px] text-slate-400 hover:text-white flex items-center gap-1"
                      >
                        <Trash2 className="w-3 h-3" /> Descartar
                      </Button>
                    )}
                    {t.status === "discarded" && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => decide(t, "restore")}
                        disabled={busyId !== null}
                        className="h-7 px-2 text-[11px] text-slate-400 hover:text-white flex items-center gap-1"
                      >
                        <Undo2 className="w-3 h-3" /> Recuperar
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
